import {observer} from 'mobx-react-lite'
import React, {useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {AvatarBase} from '@components/common/base/avatar'
import {LinkButton, LinkButtonNew} from '@components/common/base/buttons'
import {Loader} from '@components/common/base/loader'
import {TableHeadCell, TableHeadRow} from '@components/common/base/table'
import {TableCell} from '@components/common/base/table/table-cell'
import {Highlight} from '@components/common/highlight/highlight'
import {TABLE_FIRST_COL_WIDTH} from '@components/dashboard/company-users/users-table'
import {
  PendingRegistration,
  pendingRegistrationToUserProxy
} from '@components/dashboard/user-fields'
import {pendingCompaniesAdminStore} from './pending-companies-admin-store'
import {useCompanyUsersStore} from './users-store'

export const RegistrationsTable = observer(() => {
  const [state] = useCompanyUsersStore()

  const registrations = pendingCompaniesAdminStore.getTablePendingRegistrations(
    state.filterQuery
  )

  if (!registrations.length) {
    return null
  }

  const left =
    registrations.length - pendingCompaniesAdminStore.state.tableMaxShow

  return (
    <div>
      <TableHeader />
      {registrations
        .slice(0, pendingCompaniesAdminStore.state.tableMaxShow)
        .map(item => (
          <TableRow
            key={item.userId}
            item={item}
            highlight={state.filterQuery.trim()}
          />
        ))}
      {left > 0 && (
        <LinkButton
          onClick={pendingCompaniesAdminStore.tableShowMore}
          size="small"
          icon="IconDotsCircleHorizontal"
        >
          {Lang.msg.showMore2} (+{left})
        </LinkButton>
      )}
    </div>
  )
})

const HeaderCell = tw(TableHeadCell)`
  cursor-default
  flex-1
  flex-col
  !px-16
  !items-start
`

const TableHeader = () => {
  return (
    <TableHeadRow className="mt-16 -mx-16">
      <HeaderCell minWidth={TABLE_FIRST_COL_WIDTH} maxWidth={'266px'}>
        {Lang.msg.companyDashboard.employee}
      </HeaderCell>
      <HeaderCell minWidth={TABLE_FIRST_COL_WIDTH} maxWidth={'266px'}>
        {Lang.msg.email}
      </HeaderCell>
    </TableHeadRow>
  )
}

const RowCell = tw(TableCell)`
  cursor-default
  flex-1
  items-center
  px-16
`

const TableRow = observer(
  ({item, highlight}: {item: PendingRegistration; highlight: string}) => {
    const user = useMemo(() => pendingRegistrationToUserProxy(item), [item])

    return (
      <div className="group w-full flex h-32 hover:bg-interactive-l100">
        <RowCell
          className="gap-8"
          minWidth={TABLE_FIRST_COL_WIDTH}
          maxWidth={'266px'}
        >
          <AvatarBase user={user} size={16} hideStatus hideOutline hideHint />
          <Highlight text={item.username ?? ''} highlight={highlight} />
        </RowCell>
        <RowCell minWidth={TABLE_FIRST_COL_WIDTH} maxWidth={'266px'}>
          <Highlight text={item.email ?? ''} highlight={highlight} />
        </RowCell>

        <RowCell>
          {!item.inProgress ? (
            <>
              <LinkButtonNew
                onClick={() => {
                  void pendingCompaniesAdminStore.rejectRegistrations([
                    item.userId
                  ])
                  void Tracking.send(
                    TrackingEvent.companyLinkRejectUserRegistrationViaUserList
                  )
                }}
                className="ml-auto py-8 px-12"
                color="notification-primary"
              >
                {Lang.msg.users.rejectBtn}
              </LinkButtonNew>
              <LinkButtonNew
                onClick={() => {
                  void pendingCompaniesAdminStore.acceptRegistration(
                    item.userId
                  )
                  void Tracking.send(
                    TrackingEvent.companyLinkConfirmUserRegistrationViaUserList
                  )
                }}
                className="py-8 px-12"
                color="primary"
              >
                {Lang.msg.users.acceptBtn}
              </LinkButtonNew>
            </>
          ) : (
            <span className="ml-auto">
              <Loader size="xsmall" />
            </span>
          )}
        </RowCell>
      </div>
    )
  }
)
